/**
 * useSmartPaste.ts - React hook for SmartPaste
 * 
 * CHANGE HISTORY:
 * - 251110_0127 [pending] danielbeer: Initial creation
 * 
 * USAGE:
 * ```tsx
 * function MyComponent() {
 *   const smartPaste = useSmartPaste({
 *     enabled: true,
 *     autoStart: true
 *   });
 * 
 *   return <div>Smart paste is {smartPaste.isMonitoring ? 'active' : 'inactive'}</div>;
 * }
 * ```
 */

import { useEffect, useRef, useState } from 'react';
import { SmartPaste, type SmartPasteOptions } from '../core/SmartPaste';
import { useActr } from '@opcentral/actr';

export interface UseSmartPasteOptions {
  enabled?: boolean;
  autoStart?: boolean;
  translatr?: any;
  contextr?: any;
  promptr?: any;
  selectr?: any;
}

export interface UseSmartPasteReturn {
  smartPaste: SmartPaste | null;
  isMonitoring: boolean;
  start: () => void;
  stop: () => void;
  toggle: () => void;
}

/**
 * React hook for SmartPaste
 */
export function useSmartPaste(options: UseSmartPasteOptions = {}): UseSmartPasteReturn {
  const {
    enabled = true,
    autoStart = false,
    translatr,
    contextr,
    promptr,
    selectr
  } = options;

  const actr = useActr();
  const smartPasteRef = useRef<SmartPaste | null>(null);
  const [isMonitoring, setIsMonitoring] = useState(false);

  // Initialize SmartPaste
  useEffect(() => {
    if (!enabled) return;

    const smartPasteOptions: SmartPasteOptions = {
      actr,
      translatr,
      contextr,
      promptr,
      selectr
    };

    smartPasteRef.current = new SmartPaste(smartPasteOptions);

    if (autoStart) {
      smartPasteRef.current.startMonitoring();
      setIsMonitoring(true);
    }

    return () => {
      if (smartPasteRef.current) {
        smartPasteRef.current.stopMonitoring();
        smartPasteRef.current = null;
      }
    };
  }, [enabled, autoStart, actr, translatr, contextr, promptr, selectr]);

  const start = () => {
    if (smartPasteRef.current && !isMonitoring) {
      smartPasteRef.current.startMonitoring();
      setIsMonitoring(true);
    }
  };

  const stop = () => {
    if (smartPasteRef.current && isMonitoring) {
      smartPasteRef.current.stopMonitoring();
      setIsMonitoring(false);
    }
  };

  const toggle = () => {
    if (isMonitoring) {
      stop();
    } else {
      start();
    }
  };

  return {
    smartPaste: smartPasteRef.current,
    isMonitoring,
    start,
    stop,
    toggle
  };
}
